import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import kc_v from '../kc_new1.jpg';

const ModernNavbar = ({ onDrawerToggle, isDarkMode, onToggleTheme, drawerOpen }) => {
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [notificationMenuAnchor, setNotificationMenuAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const username = localStorage.getItem('username');
  const role = localStorage.getItem('role');

  useEffect(() => {
    setNotifications([
      { id: 1, message: 'New complaint submitted', time: '2 min ago', read: false },
      { id: 2, message: 'Category updated successfully', time: '1 hour ago', read: false },
      { id: 3, message: 'Backup completed', time: '3 hours ago', read: true }
    ]);
  }, []);

  const handleUserMenuOpen = (event) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);
  const handleNotificationMenuOpen = (event) => setNotificationMenuAnchor(event.currentTarget);
  const handleNotificationMenuClose = () => setNotificationMenuAnchor(null);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('username');
    handleUserMenuClose();
    navigate('/login');
  };

  const unreadNotifications = notifications.filter(n => !n.read).length;

  return (
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{ 
        zIndex: theme.zIndex.drawer + 1,
        background: isDarkMode 
          ? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 50%, #1a1a1a 100%)'
          : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #2196f3 100%)',
        borderBottom: '1px solid',
        borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.15)',
        backdropFilter: 'blur(20px)',
        backgroundColor: alpha(isDarkMode ? '#1a1a1a' : theme.palette.primary.main, 0.95),
        boxShadow: isDarkMode 
          ? '0 4px 20px rgba(0,0,0,0.3), 0 1px 3px rgba(0,0,0,0.2)'
          : '0 4px 20px rgba(25,118,210,0.15), 0 1px 3px rgba(25,118,210,0.1)'
      }}
    >
      <Toolbar sx={{ 
        minHeight: { xs: 64, md: 70 }, 
        px: { xs: 2, md: 3 },
        py: { xs: 1, md: 1.5 }
      }}>
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            color="inherit"
            onClick={onDrawerToggle}
            sx={{
              backgroundColor: drawerOpen ? alpha('#ffffff', 0.15) : alpha('#ffffff', 0.08),
              '&:hover': { 
                backgroundColor: alpha('#ffffff', 0.2),
                transform: 'scale(1.05)'
              },
              display: { xs: 'flex', md: 'none' },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: drawerOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              '&:active': {
                transform: drawerOpen ? 'rotate(90deg) scale(0.95)' : 'rotate(0deg) scale(0.95)'
              },
              borderRadius: 2,
              width: 40,
              height: 40
            }}
            aria-label="Toggle sidebar"
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={kc_v}
              sx={{ 
                width: { xs: 40, md: 45 }, 
                height: { xs: 40, md: 45 }, 
                border: '3px solid',
                borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.3)',
                boxShadow: isDarkMode 
                  ? '0 4px 12px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.2)'
                  : '0 4px 12px rgba(25,118,210,0.3), 0 2px 4px rgba(25,118,210,0.2)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: isDarkMode 
                    ? '0 6px 16px rgba(0,0,0,0.5), 0 3px 6px rgba(0,0,0,0.3)'
                    : '0 6px 16px rgba(25,118,210,0.4), 0 3px 6px rgba(25,118,210,0.3)'
                }
              }}
            />
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  background: isDarkMode 
                    ? 'linear-gradient(45deg, #ffffff 30%, #e0e0e0 90%)'
                    : 'linear-gradient(45deg, #ffffff 30%, #f0f8ff 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: isDarkMode 
                    ? '0 1px 2px rgba(0,0,0,0.3)'
                    : '0 1px 2px rgba(25,118,210,0.2)'
                }}
              >
                KC Venugopal MP
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.9)',
                  display: { xs: 'none', sm: 'block' },
                  fontSize: { xs: '0.7rem', md: '0.75rem' },
                  fontWeight: 500,
                  letterSpacing: '0.5px'
                }}
              >
                Complaint Management System
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Center Section - Search */}
        {/* Search removed as requested */}

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
          <Tooltip title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}>
            <IconButton
              color="inherit"
              onClick={onToggleTheme}
              sx={{ 
                backgroundColor: alpha('#ffffff', 0.08),
                '&:hover': { 
                  backgroundColor: alpha('#ffffff', 0.15),
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s ease-in-out',
                borderRadius: 2,
                width: 40,
                height: 40
              }}
            >
              {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Notifications">
            <IconButton
              color="inherit"
              onClick={handleNotificationMenuOpen}
              sx={{ 
                backgroundColor: alpha('#ffffff', 0.08),
                '&:hover': { 
                  backgroundColor: alpha('#ffffff', 0.15),
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s ease-in-out',
                borderRadius: 2,
                width: 40,
                height: 40
              }}
            >
              <Badge 
                badgeContent={unreadNotifications} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: isDarkMode ? '#ff4444' : '#f44336',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem'
                  }
                }}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {username && (
            <Button
              color="inherit"
              onClick={handleUserMenuOpen}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: alpha('#ffffff', 0.08),
                borderRadius: 2,
                px: 2,
                py: 1,
                '&:hover': { 
                  backgroundColor: alpha('#ffffff', 0.15),
                  transform: 'scale(1.02)'
                },
                transition: 'all 0.3s ease-in-out',
                border: '1px solid',
                borderColor: alpha('#ffffff', 0.1)
              }}
            >
              <Avatar 
                sx={{ 
                  width: 28, 
                  height: 28, 
                  mr: 1,
                  backgroundColor: isDarkMode ? '#424242' : '#1976d2',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}
              >
                {username.charAt(0).toUpperCase()}
              </Avatar>
              <Typography 
                sx={{ 
                  display: { xs: 'none', sm: 'block' },
                  color: 'inherit',
                  fontWeight: 500
                }}
              >
                {username}
              </Typography>
            </Button>
          )}
        </Box>
      </Toolbar>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationMenuAnchor}
        open={Boolean(notificationMenuAnchor)}
        onClose={handleNotificationMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 8,
          sx: { 
            borderRadius: 3, 
            minWidth: 320, 
            mt: 1,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #2d2d2d 0%, #1e1e1e 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            boxShadow: isDarkMode 
              ? '0 8px 32px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.2)'
              : '0 8px 32px rgba(0,0,0,0.1), 0 4px 16px rgba(0,0,0,0.05)'
          }
        }}
      >
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid',
          borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(25,118,210,0.02)'
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600,
            color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'text.primary'
          }}>
            Notifications
          </Typography>
        </Box>
        
        {notifications.map((notification) => (
          <MenuItem 
            key={notification.id} 
            sx={{ 
              py: 1.5, 
              px: 2,
              '&:hover': {
                backgroundColor: isDarkMode 
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(25,118,210,0.05)'
              },
              transition: 'background-color 0.2s ease-in-out'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: notification.read ? 400 : 600,
                  color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'text.primary'
                }}
              >
                {notification.message}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'text.secondary', 
                  mt: 0.5 
                }}
              >
                {notification.time}
              </Typography>
            </Box>
            {!notification.read && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: isDarkMode ? '#4caf50' : theme.palette.primary.main,
                  ml: 1,
                  boxShadow: isDarkMode 
                    ? '0 0 8px rgba(76,175,80,0.4)'
                    : '0 0 8px rgba(25,118,210,0.3)'
                }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 8,
          sx: { 
            borderRadius: 3, 
            minWidth: 200, 
            mt: 1,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #2d2d2d 0%, #1e1e1e 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            boxShadow: isDarkMode 
              ? '0 8px 32px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.2)'
              : '0 8px 32px rgba(0,0,0,0.1), 0 4px 16px rgba(0,0,0,0.05)'
          }
        }}
      >
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid',
          borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(25,118,210,0.02)'
        }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600,
              color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'text.primary'
            }}
          >
            {username}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: isDarkMode ? '#4caf50' : '#4caf50',
                boxShadow: isDarkMode 
                  ? '0 0 6px rgba(76,175,80,0.4)'
                  : '0 0 6px rgba(76,175,80,0.3)'
              }}
            />
            {role} • Active
          </Typography>
        </Box>
        
        <MenuItem 
          onClick={() => { navigate('/profile'); handleUserMenuClose(); }}
          sx={{
            '&:hover': {
              backgroundColor: isDarkMode 
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(25,118,210,0.05)'
            },
            transition: 'background-color 0.2s ease-in-out'
          }}
        >
          <PersonIcon sx={{ mr: 2, color: isDarkMode ? '#4caf50' : 'primary.main' }} />
          Profile
        </MenuItem>
        
        <MenuItem 
          onClick={() => { navigate('/settings'); handleUserMenuClose(); }}
          sx={{
            '&:hover': {
              backgroundColor: isDarkMode 
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(25,118,210,0.05)'
            },
            transition: 'background-color 0.2s ease-in-out'
          }}
        >
          <SettingsIcon sx={{ mr: 2, color: isDarkMode ? '#4caf50' : 'primary.main' }} />
          Settings
        </MenuItem>
        
        <Divider sx={{ 
          borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          my: 0.5
        }} />
        
        <MenuItem 
          onClick={handleLogout} 
          sx={{ 
            color: isDarkMode ? '#ff6b6b' : 'error.main',
            '&:hover': {
              backgroundColor: isDarkMode 
                ? 'rgba(255,107,107,0.1)'
                : 'rgba(244,67,54,0.05)'
            },
            transition: 'background-color 0.2s ease-in-out'
          }}
        >
          <ExitToAppIcon sx={{ mr: 2 }} />
          Logout
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default ModernNavbar; 